import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Card,
  CardContent,
  Zoom,
  Fade,
  Tooltip,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ThunderstormIcon from "@mui/icons-material/Thunderstorm";
import CloudIcon from "@mui/icons-material/Cloud";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import WeatherWizShareModal from "./WeatherWizShareModal";
import { GameHeader } from "../WordyVerse/Components/GameBoard/GameHeader";
import { generateDailyPuzzle } from "./puzzleGenerator";
import { HelpCircle } from "lucide-react";
import WeatherWizHowTo from "./WeatherWizHowTo";
import GameControls from "./GameControls";

// Animation keyframes
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.08); }
  100% { transform: scale(1); }
`;

const sparkle = keyframes`
  0% { box-shadow: 0 0 0 rgba(245, 166, 35, 0); }
  50% { box-shadow: 0 0 15px rgba(245, 166, 35, 0.7); }
  100% { box-shadow: 0 0 0 rgba(245, 166, 35, 0); }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

// Add shake animation
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
`;

// Styled components
const GameContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(2),
  flex: 1,
  width: "100%",
  maxWidth: "100%",
}));

const GameCard = styled(Card)(({ theme }) => ({
  width: "100%",
  maxWidth: 520,
  margin: "0 auto",
  overflow: "visible",
  position: "relative",
  backgroundColor: "#FFFFFF",
  boxShadow: "none",
  border: "none",
  [theme.breakpoints.up("md")]: {
    maxWidth: 600,
  },
}));

const DateDisplay = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(2),
}));

const StatsBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  marginBottom: theme.spacing(2),
  gap: theme.spacing(6),
  "& .MuiTypography-root": {
    fontSize: "1rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  "& .MuiSvgIcon-root": {
    fontSize: "1.2rem",
  },
}));

const GridContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "40px repeat(6, minmax(35px, 50px))",
  gap: 4,
  alignItems: "center",
  marginBottom: theme.spacing(2),
  justifyContent: "center",
  margin: "0 auto",
  [theme.breakpoints.up("sm")]: {
    gridTemplateColumns: "40px repeat(6, 50px)",
    gap: 6,
  },
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "50px repeat(6, 55px)",
    gap: 8,
  },
}));

const Label = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCorrect",
})(({ theme, isCorrect }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: "bold",
  padding: theme.spacing(1),
  fontSize: "1.2rem",
  backgroundColor: isCorrect ? theme.palette.success.main : "#f5f5f5",
  color: isCorrect ? "white" : "black",
  borderRadius: "4px",
  width: "100%",
  height: "100%",
}));

const LiveCount = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCorrect",
})(({ theme, isCorrect }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: "bold",
  padding: theme.spacing(1),
  fontSize: "1.1rem",
  color: isCorrect ? theme.palette.success.main : "#FF6B6B",
}));

const InfoLink = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(2),
  fontSize: "0.85rem",
  cursor: "pointer",
  "&:hover": {
    color: theme.palette.primary.main,
  },
}));

// Styled Sun and Cloud icons to replace emojis
const SunIcon = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60%",
  height: "60%",
  borderRadius: "50%",
  backgroundColor: "#FFCC33",
  boxShadow: "0 0 0 4px #F5A623, 0 0 10px rgba(245, 166, 35, 0.5)",
}));

const SunnyAnimation = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1,
  animation: `${sparkle} 0.6s ease-out forwards`,
}));

// Update the Cell styled component
const Cell = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isSunny" &&
    prop !== "isHint" &&
    prop !== "isAnimating" &&
    prop !== "isError",
})(({ theme, isSunny, isHint, isAnimating, isError }) => ({
  width: "100%",
  paddingBottom: "100%",
  background: isSunny ? "#FFFBEB" : "#F0F8FF",
  position: "relative",
  border: "2px solid",
  borderColor: isError ? theme.palette.error.main : "#000",
  borderRadius: theme.shape.borderRadius,
  cursor: "pointer",
  transition: "all 0.2s ease-out",
  boxShadow: "4px 4px 0px rgba(0, 0, 0, 0.3)",
  animation: isError ? `${shake} 0.3s ease-in-out` : "none",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "5px 5px 0px rgba(0, 0, 0, 0.4)",
  },
  ...(isHint && {
    boxShadow: `0 0 0 2px ${theme.palette.success.main}`,
  }),
  ...(isAnimating && {
    animation: `${pulse} 0.3s ease-out`,
  }),
  [theme.breakpoints.up("md")]: {
    borderRadius: 6,
  },
}));

const Controls = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(2),
  margin: theme.spacing(3, 0, 2),
  flexDirection: "row",
  flexWrap: "wrap",
  [theme.breakpoints.up("sm")]: {
    gap: theme.spacing(3),
  },
}));

const TimerDisplay = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  "& .MuiTypography-root": {
    fontWeight: "bold",
    color: theme.palette.text.primary,
  },
}));

// Success animation component
const SuccessMessage = styled(Box)(({ theme }) => ({
  animation: `${bounce} 1s ease-in-out`,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));

// Default grid for the game
const defaultGrid = [
  [1, 0, 0, 1, 1, 0],
  [0, 1, 1, 0, 0, 1],
  [1, 1, 0, 0, 1, 0],
  [0, 0, 1, 1, 0, 1],
  [1, 0, 1, 0, 1, 0],
  [0, 1, 0, 1, 0, 1],
];

const size = 6;

// Weather types mapping
const WEATHER_TYPES = {
  SUNNY: {
    code: 1000,
    icon: (props) => (
      <WbSunnyIcon {...props} sx={{ color: "#FFB300", ...props?.sx }} />
    ),
    label: "sun",
    description: "Fill the grid with suns",
  },
  CLOUDY: {
    code: 1003,
    icon: (props) => (
      <CloudIcon {...props} sx={{ color: "#78909C", ...props?.sx }} />
    ),
    label: "cloud",
    description: "Fill the grid with clouds",
  },
  RAINY: {
    code: 1063,
    icon: (props) => (
      <ThunderstormIcon {...props} sx={{ color: "#546E7A", ...props?.sx }} />
    ),
    label: "rain",
    description: "Fill the grid with rain",
  },
  SNOWY: {
    code: 1204,
    icon: (props) => (
      <AcUnitIcon {...props} sx={{ color: "#90CAF9", ...props?.sx }} />
    ),
    label: "snow",
    description: "Fill the grid with snow",
  },
};

// Helper function to check for consecutive suns
const hasConsecutiveSuns = (grid, row, col) => {
  const directions = [
    [0, 1], // horizontal
    [1, 0], // vertical
  ];

  for (const [dx, dy] of directions) {
    let consecutiveCount = 0;

    // Check in both directions
    for (let i = 0; i < 2; i++) {
      let currentDx = i === 0 ? dx : -dx;
      let currentDy = i === 0 ? dy : -dy;
      let r = row + currentDx;
      let c = col + currentDy;

      while (r >= 0 && r < size && c >= 0 && c < size) {
        if (grid[r][c] === 1) {
          consecutiveCount++;
          if (consecutiveCount >= 2) return true; // Found 2 consecutive suns
        } else {
          break;
        }
        r += currentDx;
        c += currentDy;
      }
    }
  }
  return false;
};

function WeatherWiz({ showTestingControls = false }) {
  // State variables
  const [correctGrid, setCorrectGrid] = useState(defaultGrid);
  const [currentGrid, setCurrentGrid] = useState(
    Array.from({ length: size }, () => Array(size).fill(0))
  );
  const [rowClues, setRowClues] = useState(Array(size).fill(0));
  const [colClues, setColClues] = useState(Array(size).fill(0));
  const [rowCounts, setRowCounts] = useState(Array(size).fill(0));
  const [colCounts, setColCounts] = useState(Array(size).fill(0));
  const [seconds, setSeconds] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [showTutorial, setShowTutorial] = useState(true);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [hintCells, setHintCells] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [progress, setProgress] = useState(0);
  const [resultTable, setResultTable] = useState(null);
  const [animatingCell, setAnimatingCell] = useState(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [previewCell, setPreviewCell] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [weather, setWeather] = useState(null);
  const [mockWeather, setMockWeather] = useState(null);
  const [errorCell, setErrorCell] = useState(null);
  const WEATHER_API_KEY = "af4ee06fe2e144199f5230125251403";
  const WEATHER_API_URL = "https://api.weatherapi.com/v1/current.json";
  const [showInstructions, setShowInstructions] = useState(false);

  const timerRef = useRef(null);

  // Calculate row and column clues (number of filled cells in each row/column)
  useEffect(() => {
    if (!correctGrid || !correctGrid[0]) return; // Add safety check

    const rowClues = correctGrid.map((row) =>
      row.reduce((sum, cell) => sum + cell, 0)
    );
    const colClues = Array(size)
      .fill(0)
      .map((_, colIndex) => {
        let count = 0;
        for (let rowIndex = 0; rowIndex < size; rowIndex++) {
          if (
            correctGrid[rowIndex] &&
            typeof correctGrid[rowIndex][colIndex] !== "undefined"
          ) {
            count += correctGrid[rowIndex][colIndex];
          }
        }
        return count;
      });

    setRowClues(rowClues);
    setColClues(colClues);
  }, [correctGrid]);

  // Add effect to check for win condition
  useEffect(() => {
    // Don't check for win if game hasn't started or there's no valid data
    if (!hasStarted || !rowClues.length || !colClues.length) return;

    // Only check for win if all numbers are valid
    const hasValidNumbers =
      rowCounts.every((count) => typeof count === "number") &&
      colCounts.every((count) => typeof count === "number") &&
      rowClues.every((clue) => typeof clue === "number") &&
      colClues.every((clue) => typeof clue === "number");

    if (!hasValidNumbers) return;

    // Check each row and column match exactly
    const rowsMatch = rowCounts.every((count, i) => {
      const matches = count === rowClues[i];
      return matches;
    });

    const colsMatch = colCounts.every((count, i) => {
      const matches = count === colClues[i];
      return matches;
    });

    const isComplete = rowsMatch && colsMatch;

    // Calculate progress regardless of completion
    const totalCorrectRowsAndCols =
      rowCounts.filter((count, i) => count === rowClues[i]).length +
      colCounts.filter((count, i) => count === colClues[i]).length;

    setProgress((totalCorrectRowsAndCols / (size * 2)) * 100);

    // Set game as completed if all match
    if (isComplete && !gameCompleted) {
      setGameCompleted(true);
      setShowSuccessAnimation(true);
      setTimeout(() => setShowSuccessAnimation(false), 2000);
    }
  }, [
    rowCounts,
    colCounts,
    rowClues,
    colClues,
    gameCompleted,
    hasStarted,
    size,
  ]);

  // Calculate current row and column counts
  useEffect(() => {
    if (!currentGrid || !currentGrid[0]) return;

    const newRowCounts = currentGrid.map((row) =>
      row.reduce((sum, cell) => sum + (cell || 0), 0)
    );
    const newColCounts = Array(size)
      .fill(0)
      .map((_, colIndex) => {
        let count = 0;
        for (let rowIndex = 0; rowIndex < size; rowIndex++) {
          if (
            currentGrid[rowIndex] &&
            typeof currentGrid[rowIndex][colIndex] !== "undefined"
          ) {
            count += currentGrid[rowIndex][colIndex] || 0;
          }
        }
        return count;
      });

    setRowCounts(newRowCounts);
    setColCounts(newColCounts);
  }, [currentGrid, size]);

  // Timer effect
  useEffect(() => {
    if (hasStarted && !gameCompleted) {
      timerRef.current = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds + 1);
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [hasStarted, gameCompleted]);

  // Show tutorial on first load
  useEffect(() => {
    if (showTutorial) {
      setIsTimerRunning(false);
    } else {
      setIsTimerRunning(true);
    }
  }, [showTutorial, isTimerRunning]);

  // Generate a daily puzzle based on the current date
  const generateDailyPuzzleForDate = useCallback((date) => {
    const dateString = date.toISOString().split("T")[0];
    try {
      const puzzle = generateDailyPuzzle(dateString);
      setCorrectGrid(puzzle.solution);
      setRowClues(puzzle.rowConstraints);
      setColClues(puzzle.colConstraints);
    } catch (error) {
      console.error("Failed to generate puzzle:", error);
      // Fallback to default grid if generation fails
      setCorrectGrid(defaultGrid);
    }
  }, []);

  // Update puzzle when date changes
  useEffect(() => {
    generateDailyPuzzleForDate(currentDate);
  }, [currentDate, generateDailyPuzzleForDate]);

  // Reset the game with a new random grid
  const resetGame = useCallback(() => {
    // Generate a new puzzle for the current date
    generateDailyPuzzleForDate(currentDate);

    // Reset all game state
    setCurrentGrid(Array.from({ length: size }, () => Array(size).fill(0)));
    setHintCells([]);
    setGameCompleted(false);
    setIsCorrect(false);
    setSeconds(0);
    setHasStarted(false);
    setResultTable(null);
    setShowSuccessAnimation(false);
  }, [currentDate, generateDailyPuzzleForDate]);

  // Initialize game state
  useEffect(() => {
    resetGame();
  }, [resetGame]);

  // Toggle a cell's state with animation
  const toggleCell = useCallback(
    (row, col) => {
      if (gameCompleted) return;

      if (!hasStarted) {
        setHasStarted(true);
      }

      setCurrentGrid((prevGrid) => {
        const newGrid = [...prevGrid];
        newGrid[row] = [...newGrid[row]];

        // If we're placing a sun (1), check for consecutive suns
        if (!newGrid[row][col]) {
          newGrid[row][col] = 1;
          if (hasConsecutiveSuns(newGrid, row, col)) {
            // If placing a sun would create 3+ consecutive suns, don't allow it
            newGrid[row][col] = 0;
            setErrorCell({ row, col });
            setTimeout(() => setErrorCell(null), 300);
            return prevGrid;
          }
        } else {
          newGrid[row][col] = 0;
        }

        return newGrid;
      });

      // Set animating cell for visual feedback
      setAnimatingCell({ row, col });
      setTimeout(() => setAnimatingCell(null), 300);
    },
    [gameCompleted, hasStarted]
  );

  // Helper to check if a date is today
  const isToday = useCallback((date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  // Navigate to a different day
  const navigateDay = useCallback(
    (direction) => {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + direction);

      // Don't allow navigation to future dates
      if (direction > 0 && isToday(currentDate)) {
        return;
      }

      setCurrentDate(newDate);
      resetGame();
    },
    [currentDate, resetGame, isToday]
  );

  // Format time display
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // Show tutorial again
  const handleShowTutorial = useCallback(() => {
    setShowTutorial(true);
  }, []);

  // Handle sharing results
  const handleShare = () => {
    const date = currentDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const shareText = `🌞 Weather Wiz - ${date}\nTime: ${formatTime(
      seconds
    )}\nPlay at: flyingcometgames.com`;

    navigator.clipboard.writeText(shareText).catch(console.error);
  };

  // Add effect to show share modal when game is completed
  useEffect(() => {
    if (gameCompleted && !showShareModal) {
      setShowShareModal(true);
    }
  }, [gameCompleted]);

  // Fetch weather data
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await fetch(
          `${WEATHER_API_URL}?key=${WEATHER_API_KEY}&q=Seattle&aqi=no`
        );
        const data = await response.json();
        setWeather(data);
      } catch (error) {
        console.error("Error fetching weather:", error);
      }
    };

    fetchWeather();
  }, []);

  // Get current weather type
  const getCurrentWeatherType = useCallback(() => {
    if (mockWeather) return mockWeather;
    if (!weather?.current?.condition?.code) return WEATHER_TYPES.SUNNY;

    const code = weather.current.condition.code;
    if (code === 1000) return WEATHER_TYPES.SUNNY;
    if (code >= 1003 && code <= 1030) return WEATHER_TYPES.CLOUDY;
    if (code >= 1063 && code <= 1201) return WEATHER_TYPES.RAINY;
    if (code >= 1204 && code <= 1237) return WEATHER_TYPES.SNOWY;
    return WEATHER_TYPES.SUNNY;
  }, [weather, mockWeather]);

  const currentWeatherType = getCurrentWeatherType();

  // Add function to toggle instructions
  const toggleInstructions = () => {
    setShowInstructions(!showInstructions);
  };

  return (
    <GameContainer>
      <GameCard>
        <CardContent>
          <GameHeader
            bannerPath="/assets/customer-logos/banner/comet-chronicles.svg"
            title="Weather Wiz"
          />
          <Typography
            variant="h2"
            component="h1"
            align="center"
            sx={{
              fontSize: "2.75rem",
              fontWeight: "bold",
              mb: 4,
            }}
          >
            Weather Wiz
          </Typography>

          <GameControls
            currentDate={currentDate}
            onDateChange={navigateDay}
            weatherType={currentWeatherType}
            weather={weather}
            formatTime={formatTime}
            seconds={seconds}
            isEmbedded={false}
          />

          <GridContainer>
            <Box /> {/* Empty corner */}
            {colClues.map((clue, i) => (
              <Label
                key={`col-clue-${i}`}
                isCorrect={colCounts[i] === colClues[i]}
              >
                {clue}
              </Label>
            ))}
            {rowClues.map((clue, row) => (
              <React.Fragment key={`row-${row}`}>
                <Label isCorrect={rowCounts[row] === rowClues[row]}>
                  {clue}
                </Label>
                {Array.from({ length: size }).map((_, col) => (
                  <Cell
                    key={`cell-${row}-${col}`}
                    isSunny={currentGrid[row][col] === 1}
                    isHint={hintCells.some(
                      (hint) => hint.row === row && hint.col === col
                    )}
                    isAnimating={
                      animatingCell &&
                      animatingCell.row === row &&
                      animatingCell.col === col
                    }
                    isError={
                      errorCell &&
                      errorCell.row === row &&
                      errorCell.col === col
                    }
                    onClick={() => {
                      if (
                        !gameCompleted &&
                        !hintCells.some(
                          (hint) => hint.row === row && hint.col === col
                        )
                      ) {
                        toggleCell(row, col);
                      }
                    }}
                  >
                    {currentGrid[row][col] === 1 &&
                      currentWeatherType.icon({
                        sx: {
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          fontSize: "2rem",
                        },
                      })}
                  </Cell>
                ))}
              </React.Fragment>
            ))}
          </GridContainer>

          {showTestingControls && (
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: "2px dashed #ccc",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  color: "#666",
                  mb: 1,
                }}
              >
                Testing Controls
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  pb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 1,
                    width: "100%",
                    maxWidth: "360px",
                  }}
                >
                  {Object.entries(WEATHER_TYPES).map(([key, type]) => (
                    <Button
                      key={key}
                      onClick={() => setMockWeather(type)}
                      variant={mockWeather === type ? "contained" : "outlined"}
                      size="small"
                      startIcon={type.icon({})}
                      sx={{
                        textTransform: "capitalize",
                        bgcolor:
                          mockWeather === type ? "#f0f0f0" : "transparent",
                        color: "#666",
                        "&:hover": {
                          bgcolor: "#e0e0e0",
                        },
                      }}
                    >
                      {type.label}
                    </Button>
                  ))}
                  <Button
                    onClick={() => setMockWeather(null)}
                    variant={mockWeather === null ? "contained" : "outlined"}
                    size="small"
                    sx={{
                      textTransform: "capitalize",
                      bgcolor: mockWeather === null ? "#f0f0f0" : "transparent",
                      color: "#666",
                      "&:hover": {
                        bgcolor: "#e0e0e0",
                      },
                    }}
                  >
                    Real
                  </Button>
                  <Button
                    onClick={() => {
                      setGameCompleted(true);
                      if (!hasStarted) {
                        setHasStarted(true);
                      }
                    }}
                    sx={{
                      textTransform: "capitalize",
                      bgcolor: "#f0f0f0",
                      color: "#666",
                      "&:hover": {
                        bgcolor: "#e0e0e0",
                      },
                    }}
                  >
                    Force Win
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Replace GameHint with WeatherWizHowTo */}
          <WeatherWizHowTo
            open={showInstructions}
            onClose={toggleInstructions}
            weatherType={currentWeatherType}
            isEmbedded={false}
          />
        </CardContent>
      </GameCard>

      <WeatherWizShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={handleShare}
        timeFormatted={formatTime(seconds)}
        isCorrect={gameCompleted}
      />
    </GameContainer>
  );
}

WeatherWiz.defaultProps = {
  showTestingControls: false,
};

export default WeatherWiz;
